import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import itLocale from 'date-fns/locale/it';
import api from '../../utils/api';

const DepositPaymentDialog = ({ open, onClose, deposit, onSave }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    paymentMethodId: '',
    accountId: '',
    paymentDate: new Date()
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [paymentMethodsRes, accountsRes] = await Promise.all([
          api.get('/payment-methods'),
          api.get('/accounts')
        ]);
        setPaymentMethods(paymentMethodsRes.data);
        setAccounts(accountsRes.data);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Errore nel caricamento dei dati');
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchData();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setFormData({
        paymentMethodId: '',
        accountId: '',
        paymentDate: new Date()
      });
      setError('');
      setLoading(false);
    }
  }, [open]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      paymentDate: date
    }));
  };

  const handleSubmit = () => {
    if (!formData.paymentMethodId || !formData.accountId) {
      setError('Tutti i campi sono obbligatori');
      return;
    }

    onSave({
      paymentMethodId: parseInt(formData.paymentMethodId),
      accountId: parseInt(formData.accountId),
      paymentDate: format(formData.paymentDate, 'yyyy-MM-dd')
    });
  };

  const handleCancel = () => {
    setFormData({
      paymentMethodId: '',
      accountId: '',
      paymentDate: new Date()
    });
    if (onClose) onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Info Acconto</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Importo Acconto: €{deposit}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={!formData.paymentMethodId}>
                <InputLabel>Metodo di Pagamento *</InputLabel>
                <Select
                  name="paymentMethodId"
                  value={formData.paymentMethodId}
                  onChange={handleInputChange}
                  label="Metodo di Pagamento *"
                >
                  {paymentMethods.map(method => (
                    <MenuItem key={method.id} value={method.id}>
                      {method.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={!formData.accountId}>
                <InputLabel>Conto *</InputLabel>
                <Select
                  name="accountId"
                  value={formData.accountId}
                  onChange={handleInputChange}
                  label="Conto *"
                >
                  {accounts.map(account => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={itLocale}>
                <DatePicker
                  label="Data Pagamento *"
                  value={formData.paymentDate}
                  onChange={(date) => handleInputChange({ target: { name: 'paymentDate', value: date }})}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            {error && (
              <Grid item xs={12}>
                <Typography color="error">{error}</Typography>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Annulla</Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.paymentMethodId || !formData.accountId}
        >
          Salva
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DepositPaymentDialog; 